import type { GroupedOption, SelectOption } from '~/shared/utils/types';

/** The `field` values `/api/v1/volumes/autocomplete` accepts from the volume filter panel. */
export type VolumeAutocompleteField =
  | 'clusterName'
  | 'instanceId'
  | 'volumeId'
  | 'nodeGroupName'
  | 'region'
  | 'diskType';

export interface VolumeAutocompleteQueryOptions {
  /**
   * Whether a whitespace-only pattern should still reach the API as `pattern=`.
   *
   * Node group needs it: its `defaultQuery` is a single space precisely so that opening the
   * section lists everything, and trimming that away here would make the section fetch nothing.
   * The prefix-seeded fields ('eks', 'i-', 'vol-') never send blank, so they bail instead — a
   * user who has typed only spaces gets no request rather than the whole tenant.
   */
  allowBlankPattern?: boolean;
}

/**
 * The autocomplete query string for one field, or `null` when the pattern is too empty to send.
 *
 * Pure and separate from the component so the request shape is testable under vitest's node
 * environment — `URLSearchParams` is a Node global.
 */
export function buildVolumeAutocompleteQuery(
  field: VolumeAutocompleteField,
  pattern: string,
  accounts?: string,
  { allowBlankPattern = false }: VolumeAutocompleteQueryOptions = {}
): string | null {
  const trimmed = pattern.trim();

  if (allowBlankPattern ? !pattern : !trimmed) {
    return null;
  }

  const params = new URLSearchParams({ field, pattern: trimmed });
  if (accounts) params.set('accounts', accounts);

  return params.toString();
}

/**
 * The API's flat `values` as the single grouped option list `SearchFilterBase` renders.
 *
 * Values are trimmed and blanks dropped for every field. Only cluster name and node group did
 * this before; instance and volume ids have no whitespace to lose, so applying it uniformly
 * costs nothing and removes a per-field knob.
 */
export function toVolumeAutocompleteOptions(
  values: string[] | undefined,
  field: VolumeAutocompleteField,
  groupLabel: string
): GroupedOption[] {
  const names = (values || []).map((value) => value.trim()).filter(Boolean);

  if (names.length === 0) {
    return [];
  }

  return [
    {
      label: groupLabel,
      options: names.map(
        (name): SelectOption => ({ id: name, name, group: field })
      ),
    },
  ];
}
